import * as React from "react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";
import {UserContext, UserContextI} from "../../UserContext";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {
    Button,
    Checkbox,
    Grid,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useToast
} from "@chakra-ui/react";
import {BiArrowBack, BiCog, BiEdit, BiPlus, BiTrashAlt} from "react-icons/bi";
import {useSearchParams} from "react-router-dom";
import AuthButton from "../../auth/AuthButton";
import {getOrganizationsQuery} from "../../components/area/areaQueries";
import {
    createManagedUserMutation,
    deleteManagedUserMutation,
    getUsersQuery,
    updateManagedUserMutation
} from "../../components/user/userQueries";
import {
    isAdministrativeRole,
    isRootAdminRole,
    USER_ROLE_ADMINISTRATOR,
    USER_ROLE_EDITOR
} from "../../auth/types";

type ManagedUser = {
    id: string
    email: string
    fullName?: string
    role?: string
    organizations: string[]
}

type OrganizationOption = {
    id: string
    name: string
}

type DialogMode = "create" | "edit";

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

const UserManagement = () => {
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {confirm} = useConfirmDialog();
    const toast = useToast();
    const [search, setSearch] = useSearchParams();

    const [getUsers, {data: usersData, loading: loadingUsers}] = useLazyQuery(getUsersQuery);
    const [getOrganizations, {data: organizationsData}] = useLazyQuery(getOrganizationsQuery);
    const [createManagedUser, {loading: creatingUser}] = useMutation(createManagedUserMutation);
    const [updateManagedUser, {loading: updatingUser}] = useMutation(updateManagedUserMutation);
    const [deleteManagedUser, {loading: deletingUser}] = useMutation(deleteManagedUserMutation);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<DialogMode>("create");
    const [editingUserId, setEditingUserId] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [fullName, setFullName] = useState<string>("");
    const [role, setRole] = useState<string>(USER_ROLE_EDITOR);

    const [orgDialogUser, setOrgDialogUser] = useState<ManagedUser | undefined>(undefined);
    const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);

    const currentUser = userContext.getUser();
    const isAdministrativeUser = isAdministrativeRole(currentUser?.role);
    const isRootAdmin = isRootAdminRole(currentUser?.role);

    const users = useMemo<ManagedUser[]>(() => {
        const list = usersData?.getUsers || [];
        return list
            .map((user: any) => ({
                id: user?.id,
                email: user?.email || "",
                fullName: user?.fullName || "",
                role: user?.role || USER_ROLE_EDITOR,
                organizations: Array.isArray(user?.organizations) ? user.organizations : []
            }))
            .filter((user: ManagedUser) => !!user.id)
            .sort((a: ManagedUser, b: ManagedUser) => a.email.localeCompare(b.email, undefined, {sensitivity: "base"}));
    }, [usersData]);

    const organizationOptions = useMemo<OrganizationOption[]>(() => {
        const list = organizationsData?.getOrganizations || [];
        return list
            .map((org: any) => ({
                id: org?.id,
                name: org?.name || org?.id || ""
            }))
            .filter((org: OrganizationOption) => !!org.id)
            .sort((a: OrganizationOption, b: OrganizationOption) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}));
    }, [organizationsData]);

    const organizationNameById = useMemo(() => {
        const map = new Map<string, string>();
        organizationOptions.forEach((org) => map.set(org.id, org.name));
        return map;
    }, [organizationOptions]);

    const reload = useCallback(() => {
        if (!isAdministrativeUser) {
            return;
        }
        getUsers({
            fetchPolicy: "network-only"
        });
        getOrganizations({
            variables: {
                organizationIds: isRootAdmin ? [] : (currentUser?.organizations || [])
            },
            fetchPolicy: "network-only"
        });
    }, [currentUser?.organizations, getOrganizations, getUsers, isAdministrativeUser, isRootAdmin]);

    useEffect(() => {
        reload();
    }, [reload]);

    const backToAreas = () => {
        search.delete("view");
        setSearch(search);
    };

    const openOrganizationManagement = () => {
        search.set("view", "organizations");
        setSearch(search);
    };

    const openPlanManagement = () => {
        search.set("view", "plans");
        setSearch(search);
    };

    const openUserManagement = () => {
        search.set("view", "users");
        setSearch(search);
    };

    const closeDialog = () => {
        if (creatingUser || updatingUser) {
            return;
        }
        setDialogOpen(false);
        setDialogMode("create");
        setEditingUserId("");
        setEmail("");
        setFullName("");
        setRole(USER_ROLE_EDITOR);
    };

    const openCreateDialog = () => {
        setDialogMode("create");
        setEditingUserId("");
        setEmail("");
        setFullName("");
        setRole(USER_ROLE_EDITOR);
        setDialogOpen(true);
    };

    const openEditDialog = (user: ManagedUser) => {
        setDialogMode("edit");
        setEditingUserId(user.id);
        setEmail(user.email || "");
        setFullName(user.fullName || "");
        setRole(user.role || USER_ROLE_EDITOR);
        setDialogOpen(true);
    };

    const handleSaveUser = async () => {
        const trimmedEmail = email.trim().toLowerCase();
        if (!isValidEmail(trimmedEmail)) {
            return;
        }
        try {
            if (dialogMode === "create") {
                await createManagedUser({
                    variables: {
                        email: trimmedEmail,
                        fullName: fullName.trim() || null,
                        role: role
                    }
                });
            } else {
                await updateManagedUser({
                    variables: {
                        id: editingUserId,
                        email: trimmedEmail,
                        fullName: fullName.trim() || null,
                        role: role
                    }
                });
            }
            closeDialog();
            reload();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to save user.";
            toast({
                title: "User save failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    const handleDeleteUser = async (user: ManagedUser) => {
        const approved = await confirm({
            title: "Delete user",
            body: `Delete "${user.email}"?`,
            confirmText: "Delete",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await deleteManagedUser({
                variables: {
                    id: user.id
                }
            });
            reload();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to delete user.";
            toast({
                title: "User delete failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    const openOrgDialog = (user: ManagedUser) => {
        setOrgDialogUser(user);
        setSelectedOrganizations([...(user.organizations || [])]);
    };

    const closeOrgDialog = () => {
        if (updatingUser) {
            return;
        }
        setOrgDialogUser(undefined);
        setSelectedOrganizations([]);
    };

    const toggleOrganization = (organizationId: string, checked: boolean) => {
        setSelectedOrganizations((prev) => {
            if (checked) {
                return prev.includes(organizationId) ? prev : [...prev, organizationId];
            }
            return prev.filter((id) => id !== organizationId);
        });
    };

    const saveOrganizationRelations = async () => {
        if (!orgDialogUser) {
            return;
        }
        try {
            await updateManagedUser({
                variables: {
                    id: orgDialogUser.id,
                    organizations: selectedOrganizations
                }
            });
            closeOrgDialog();
            reload();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to update organizations.";
            toast({
                title: "Update failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    if (!isAdministrativeUser) {
        return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px 1fr 40px"}>
            <Grid gridTemplateColumns={"1fr max-content"} alignItems={"center"} columnGap={2}>
                <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToAreas}>
                    Back to spaces
                </Button>
                <AuthButton/>
            </Grid>
            <div>Unauthorized</div>
            <div/>
        </Grid>;
    }

    const emailInvalid = !isValidEmail(email);

    return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px 1fr 40px"}>
        <Grid gridTemplateColumns={"max-content 1fr max-content max-content"} alignItems={"center"} columnGap={2}>
            <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToAreas} width={"fit-content"} justifySelf={"start"}>
                Back to spaces
            </Button>
            <div/>
            <Menu>
                <MenuButton
                    as={IconButton}
                    aria-label={"Management actions"}
                    icon={<BiCog/>}
                    size={"sm"}
                    variant={"outline"}
                />
                <MenuList zIndex={20}>
                    <MenuItem onClick={openUserManagement}>
                        Manage users
                    </MenuItem>
                    {isRootAdmin ? (
                        <MenuItem onClick={openOrganizationManagement}>
                            Manage organizations
                        </MenuItem>
                    ) : null}
                    {isRootAdmin ? (
                        <MenuItem onClick={openPlanManagement}>
                            Manage organization plans
                        </MenuItem>
                    ) : null}
                </MenuList>
            </Menu>
            <AuthButton/>
        </Grid>
        <div className="areaList">
            <HStack justifyContent={"space-between"} alignItems={"center"} mb={3}>
                <Text fontSize={"lg"} fontWeight={"semibold"}>Users</Text>
                <IconButton
                    aria-label={"Add user"}
                    icon={<BiPlus/>}
                    size={"sm"}
                    variant={"ghost"}
                    onClick={openCreateDialog}
                />
            </HStack>
            <Table size={"sm"} variant={"simple"}>
                <Thead>
                    <Tr>
                        <Th>Email</Th>
                        <Th>Full name</Th>
                        <Th>Role</Th>
                        <Th>Organizations</Th>
                        <Th width={"1%"} whiteSpace={"nowrap"}>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {users.map((user) => {
                        const orgNames = (user.organizations || [])
                            .map((id) => organizationNameById.get(id) || id);
                        return (
                            <Tr key={user.id}>
                                <Td>{user.email}</Td>
                                <Td>{user.fullName || ""}</Td>
                                <Td>{user.role || USER_ROLE_EDITOR}</Td>
                                <Td>{orgNames.join(", ")}</Td>
                                <Td>
                                    <HStack spacing={1}>
                                        <Button size={"xs"} variant={"ghost"} onClick={() => openOrgDialog(user)}>
                                            Organizations
                                        </Button>
                                        <Tooltip label={"Edit user"}>
                                            <IconButton
                                                aria-label={"Edit user"}
                                                icon={<BiEdit/>}
                                                size={"sm"}
                                                variant={"ghost"}
                                                onClick={() => openEditDialog(user)}
                                            />
                                        </Tooltip>
                                        <Tooltip label={"Delete user"}>
                                            <IconButton
                                                aria-label={"Delete user"}
                                                icon={<BiTrashAlt/>}
                                                size={"sm"}
                                                variant={"ghost"}
                                                colorScheme={"red"}
                                                onClick={() => handleDeleteUser(user)}
                                                isDisabled={deletingUser}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </Td>
                            </Tr>
                        );
                    })}
                    {!loadingUsers && users.length === 0 ? (
                        <Tr>
                            <Td colSpan={5}>No users</Td>
                        </Tr>
                    ) : null}
                </Tbody>
            </Table>
        </div>
        <div/>

        <Modal isOpen={dialogOpen} onClose={closeDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{dialogMode === "create" ? "Add user" : "Edit user"}</ModalHeader>
                <ModalCloseButton isDisabled={creatingUser || updatingUser}/>
                <ModalBody>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Text fontWeight={"semibold"}>Email</Text>
                            <Input
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder={"name@example.com"}
                                autoFocus
                            />
                            {emailInvalid ? <Text color={"red.500"} fontSize={"sm"}>Enter a valid email address.</Text> : null}
                        </Stack>
                        <Stack spacing={1}>
                            <Text fontWeight={"semibold"}>Full name</Text>
                            <Input
                                value={fullName}
                                onChange={(event) => setFullName(event.target.value)}
                                placeholder={"Optional"}
                            />
                        </Stack>
                        <Stack spacing={1}>
                            <Text fontWeight={"semibold"}>Role</Text>
                            <Select value={role} onChange={(event) => setRole(event.target.value)}>
                                <option value={USER_ROLE_EDITOR}>Editor</option>
                                <option value={USER_ROLE_ADMINISTRATOR}>Administrator</option>
                            </Select>
                        </Stack>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={closeDialog} isDisabled={creatingUser || updatingUser}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme={"blue"}
                        onClick={handleSaveUser}
                        isLoading={creatingUser || updatingUser}
                        isDisabled={emailInvalid}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <Modal isOpen={!!orgDialogUser} onClose={closeOrgDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Manage organizations</ModalHeader>
                <ModalCloseButton isDisabled={updatingUser}/>
                <ModalBody>
                    <Stack spacing={3}>
                        <Text fontSize={"sm"} color={"gray.600"}>
                            User: {orgDialogUser?.email}
                        </Text>
                        {organizationOptions.length === 0 ? (
                            <Text>No organizations available.</Text>
                        ) : organizationOptions.map((organization) => (
                            <Checkbox
                                key={organization.id}
                                isChecked={selectedOrganizations.includes(organization.id)}
                                onChange={(event) => toggleOrganization(organization.id, event.target.checked)}
                            >
                                {organization.name}
                            </Checkbox>
                        ))}
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={closeOrgDialog} isDisabled={updatingUser}>
                        Cancel
                    </Button>
                    <Button colorScheme={"blue"} onClick={saveOrganizationRelations} isLoading={updatingUser}>
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    </Grid>;
};

export default UserManagement;
